import { useEffect, useState, type FormEvent } from "react";
import { Camera, LineChart, Star, Trash2 } from "lucide-react";
import { useLiveQuery } from "dexie-react-hooks";
import { CATEGORIES, UNITS, de, type CategoryKey, type Item, type Unit } from "@ainkauf/core";
import { alsBlob, db } from "@/db/dexie";
import { pendingPhoto, queuePhoto, removePhoto, toggleFavorite } from "@/db/repository";
import { ItemPhoto } from "@/components/ItemPhoto";
import { navigate } from "@/lib/router";

/**
 * Blatt von unten zum Bearbeiten eines Artikels. Menge, Einheit, Notiz,
 * Kategorie, wiederkehrend. Löschen sitzt unten und ist bewusst schlicht
 * gehalten, nicht rot blinkend.
 */
export function ItemSheet({
  item,
  onSave,
  onDelete,
  onClose,
}: {
  item: Item;
  onSave: (patch: { name: string; quantity: number | null; unit: Unit | null; note: string | null; category: CategoryKey | null; is_recurring: boolean }) => Promise<void>;
  onDelete: () => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(item.quantity === null ? "" : String(item.quantity));
  const [unit, setUnit] = useState<Unit | "">(item.unit ?? "");
  const [note, setNote] = useState(item.note ?? "");
  const [category, setCategory] = useState<CategoryKey | "">(item.category ?? "");

  const [recurring, setRecurring] = useState(item.is_recurring);

  // Der Favoriten-Stern gehört zum Produkt im Katalog, nicht zum einzelnen
  // Artikel auf der Liste: er soll beim nächsten Einkauf wieder da sein.
  const product = useLiveQuery(
    async () => (item.product_id ? ((await db.products.get(item.product_id)) ?? null) : null),
    [item.product_id],
    null,
  );

  const wartendesFoto = useLiveQuery(() => pendingPhoto(item.id), [item.id], null);
  const hatFoto = wartendesFoto !== null || item.photo_path !== null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = quantity.trim().length === 0 ? null : Number.parseFloat(quantity.replace(",", "."));
    await onSave({
      name: name.trim(),
      quantity: parsed !== null && Number.isFinite(parsed) ? parsed : null,
      unit: unit === "" ? null : unit,
      note: note.trim().length === 0 ? null : note.trim(),
      category: category === "" ? null : category,
      is_recurring: recurring,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-30 flex flex-col justify-end bg-ink/40" onClick={onClose} role="presentation">
      <form
        onSubmit={(event) => void submit(event)}
        onClick={(event) => {
          event.stopPropagation();
        }}
        aria-label={de.items.edit}
        data-testid="item-sheet"
        className="flex max-h-[88dvh] flex-col gap-4 overflow-y-auto rounded-t-[var(--radius-xl)] bg-paper px-5 pt-4"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-center justify-between gap-3">
          <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <h2 className="text-xl">{de.items.edit}</h2>
          {product && (
            <button
              type="button"
              onClick={() => void toggleFavorite(product.id)}
              aria-pressed={product.is_favorite}
              aria-label={de.items.favorite}
              data-testid="sheet-favorite"
              className={`inline-flex size-11 items-center justify-center rounded-full ${
                product.is_favorite ? "bg-honey/20 text-honey" : "bg-paper-2 text-ink-3"
              }`}
            >
              <Star size={20} strokeWidth={2} aria-hidden />
            </button>
          )}
        </div>

        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.items.nameLabel}
          <input
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            data-testid="sheet-name"
            className="karte min-h-[52px] px-4 text-base text-ink outline-none"
          />
        </label>

        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1.5 text-sm text-ink-2">
            {de.items.quantityLabel}
            <input
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
              }}
              inputMode="decimal"
              data-testid="sheet-quantity"
              className="num karte min-h-[52px] px-4 text-base text-ink outline-none"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1.5 text-sm text-ink-2">
            {de.items.unitLabel}
            <select
              value={unit}
              onChange={(event) => {
                setUnit(event.target.value as Unit | "");
              }}
              data-testid="sheet-unit"
              className="karte min-h-[52px] px-3 text-base text-ink outline-none"
            >
              <option value="">{de.lists.noStore}</option>
              {UNITS.map((value) => (
                <option key={value} value={value}>
                  {de.items.units[value]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.items.categoryLabel}
          <select
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as CategoryKey | "");
            }}
            data-testid="sheet-category"
            className="karte min-h-[52px] px-3 text-base text-ink outline-none"
          >
            <option value="">{de.items.withoutCategory}</option>
            {CATEGORIES.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-sm text-ink-2">{de.items.photo}</span>
          {hatFoto && <ItemPhoto item={item} queued={wartendesFoto ? alsBlob(wartendesFoto) : null} />}
          <div className="flex gap-2">
            <label className="knopf-leise inline-flex min-h-[52px] flex-1 cursor-pointer items-center justify-center gap-2 px-4">
              <Camera size={18} strokeWidth={1.5} aria-hidden />
              {de.items.photoAdd}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                data-testid="sheet-photo"
                className="sr-only"
                onChange={(event) => {
                  const datei = event.target.files?.[0];
                  if (datei) void queuePhoto(item.id, datei);
                }}
              />
            </label>
            {hatFoto && (
              <button
                type="button"
                onClick={() => void removePhoto(item.id)}
                aria-label={de.items.photoRemove}
                data-testid="sheet-photo-remove"
                className="knopf-leise inline-flex size-[52px] shrink-0 items-center justify-center text-ink-3"
              >
                <Trash2 size={18} strokeWidth={1.5} aria-hidden />
              </button>
            )}
          </div>
        </div>

        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.items.noteLabel}
          <input
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
            data-testid="sheet-note"
            className="karte min-h-[52px] px-4 text-base text-ink outline-none"
          />
        </label>

        <button
          type="button"
          onClick={() => {
            setRecurring((value) => !value);
          }}
          aria-pressed={recurring}
          data-testid="sheet-recurring"
          className="karte flex min-h-[60px] items-center justify-between px-4 text-left"
        >
          <span>
            <span className="block text-ink">{de.items.recurring}</span>
            <span className="block text-sm text-ink-2">{de.items.recurringHint}</span>
          </span>
          <span className={`ml-3 inline-block h-7 w-12 shrink-0 rounded-full p-1 ${recurring ? "bg-herb" : "bg-paper-3"}`}>
            <span className={`block size-5 rounded-full bg-paper transition-transform ${recurring ? "translate-x-5" : ""}`} />
          </span>
        </button>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="knopf-leise min-h-[56px] flex-1 px-4"
          >
            {de.common.cancel}
          </button>
          <button
            type="submit"
            data-testid="sheet-save"
            className="knopf-haupt min-h-[56px] flex-1 px-4"
          >
            {de.common.save}
          </button>
        </div>

        {/* Nur bei einem bekannten Produkt: Ohne Katalogeintrag gibt es auch
            keinen Verlauf, und ein Weg ins Leere ist schlimmer als keiner. */}
        {item.product_id !== null && (
          <button
            type="button"
            onClick={() => {
              navigate(`/produkt/${item.product_id ?? ""}`);
            }}
            data-testid="sheet-prices"
            className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-paper-2 px-4 text-sm font-semibold text-ink-2"
          >
            <LineChart size={17} strokeWidth={2} aria-hidden />
            {de.prices.open}
          </button>
        )}

        <button
          type="button"
          onClick={() => void onDelete().then(onClose)}
          data-testid="sheet-delete"
          className="min-h-[44px] text-sm text-ink-2 underline underline-offset-4"
        >
          {de.items.deleteItem}
        </button>
      </form>
    </div>
  );
}
